import type {
  AsyncStatus,
  ScheduleListItem,
  SchedulesInConflicts,
} from 'lib-shared-types-api';
import { useEffect, useState } from 'react';

import { flowsApi } from 'apis/flows';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

type UseSchedulesResult = {
  /** The async status of the read flow schedules api endpoint. */
  readonly asyncStatus: AsyncStatus;
  /** The current page schedules. */
  readonly currentPageSchedules: readonly ScheduleListItem[];
  /** Whether there is a next page to navigate to. */
  readonly hasNextPage: boolean;
  /** Whether there is a previous page to navigate to. */
  readonly hasPreviousPage: boolean;
  /** The current list of conflicting schedules. */
  readonly schedulesInConflicts: readonly SchedulesInConflicts[];
  /** The total number of schedules. */
  readonly totalNumberOfSchedules: number;
  /** The method for navigating to the next page. */
  readonly nextPage: VoidFunction;
  /** The method for navigating to the previous page. */
  readonly previousPage: VoidFunction;
  /** The method for refetching schedules. */
  readonly refetchItems: VoidFunction;
  /** The method for refetching conflicting schedules. */
  readonly refetchSchedulesInConflicts: VoidFunction;
};

/**
 * A hook that handles fetching, updating schedules on the business hours
 * overview page.
 */
export function useSchedules(): UseSchedulesResult {
  const [currentPageSchedules, setCurrentPageSchedules] = useState<
    readonly ScheduleListItem[]
  >([]);

  const {
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    items,
    total: totalNumberOfSchedules = 0,
    nextPage,
    previousPage,
    refetchItems,
  } = usePaginatedEndpoint({
    endpoint: flowsApi.endpoints.readFlowsSchedules,
    initialArg: {},
    itemsSelector: ({ data: { schedules } }) => schedules,
    totalSelector: ({ data: { total_number_of_schedules } }) =>
      total_number_of_schedules,
  });

  const { schedulesInConflicts, refetch: refetchSchedulesInConflicts } =
    flowsApi.useReadFlowsSchedulesConflictsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        schedulesInConflicts: data?.data.schedules_in_conflicts ?? [],
      }),
    });

  // Updates local state of current page schedules that is used for
  // populating the table.
  useEffect(() => {
    setCurrentPageSchedules(items);
  }, [items]);

  return {
    asyncStatus,
    currentPageSchedules,
    hasNextPage,
    hasPreviousPage,
    schedulesInConflicts,
    totalNumberOfSchedules,
    nextPage,
    previousPage,
    refetchItems,
    refetchSchedulesInConflicts,
  };
}
